import * as React from "react"

interface UsePaginationOptions {
  defaultPageSize?: number
}

/**
 * Client-side pagination state.
 *
 * - `page` is 1-based and auto-clamps when totalRows shrinks.
 * - Changing pageSize resets to page 1.
 * - `slice(rows)` returns the current page's rows.
 */
export function usePagination(
  totalRows: number,
  { defaultPageSize = 20 }: UsePaginationOptions = {}
) {
  const [rawPage, setRawPage] = React.useState(1)
  const [pageSize, setPageSizeState] = React.useState(defaultPageSize)

  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize))
  // Clamp on the fly so the same render never shows an out-of-range page.
  const page = Math.min(Math.max(1, rawPage), pageCount)

  // Normalize stored state after totals shrink.
  React.useEffect(() => {
    if (rawPage > pageCount) setRawPage(pageCount)
  }, [rawPage, pageCount])

  const setPage = React.useCallback((next: number) => {
    setRawPage(Math.max(1, next))
  }, [])

  const setPageSize = React.useCallback((size: number) => {
    setPageSizeState(size)
    setRawPage(1)
  }, [])

  const slice = React.useCallback(
    <T,>(rows: T[]): T[] => rows.slice((page - 1) * pageSize, page * pageSize),
    [page, pageSize]
  )

  return { page, pageSize, setPage, setPageSize, pageCount, slice }
}
